import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Heart, MessageCircle, Repeat2, Eye } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { api, type CampaignBreakdown } from "../../lib/api";
import { getPlatformMeta } from "../../lib/platform-meta";
import MetricCard from "./MetricCard";

export default function CampaignAnalytics() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const [data, setData] = useState<CampaignBreakdown | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!campaignId) return;
    api.analytics
      .campaign(campaignId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [campaignId]);

  if (loading) return <p className="text-gray-400 text-sm">Loading...</p>;
  if (!data)
    return (
      <p className="text-red-600 text-sm">Failed to load campaign analytics.</p>
    );

  const platforms = Object.entries(data.byPlatform);

  // Status donut data
  const statusData = [
    { name: "Published", value: data.published, color: "#3B82F6" },
    {
      name: "Approved",
      value: data.approved - data.published,
      color: "#10B981",
    },
    { name: "Rejected", value: data.rejected, color: "#EF4444" },
    {
      name: "Pending",
      value: data.total - data.approved - data.rejected,
      color: "#D1D5DB",
    },
  ].filter((d) => d.value > 0);

  // Platform bar data
  const platformBarData = platforms.map(([platform, stats]) => ({
    platform: getPlatformMeta(platform).shortLabel,
    color: getPlatformMeta(platform).color,
    total: stats.total,
    approved: stats.approved,
    published: stats.published,
    rejected: stats.rejected,
  }));

  return (
    <div>
      <div className="mb-8">
        <Link
          to="/analytics"
          className="text-sm text-gray-500 hover:text-gray-700 mb-2 inline-block"
        >
          &larr; Back to Analytics
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Campaign analytics</h1>
        <p className="text-sm text-gray-500 mt-1">
          Campaign {campaignId?.slice(0, 8)}...
        </p>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Total posts" value={data.total} />
        <MetricCard label="Approved" value={data.approved} />
        <MetricCard label="Published" value={data.published} />
        <MetricCard label="Rejected" value={data.rejected} />
        <MetricCard
          label="Approval rate"
          value={`${Math.round(data.approvalRate * 100)}%`}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Status donut */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-800 mb-4">
            Post status breakdown
          </h2>
          {data.total === 0 ? (
            <p className="text-sm text-gray-400">No posts in this campaign.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {statusData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                    fontSize: 12,
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12 }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Platform breakdown */}
        <div className="card p-6">
          <h2 className="font-semibold text-gray-800 mb-4">
            Posts by platform
          </h2>
          {platforms.length === 0 ? (
            <p className="text-sm text-gray-400">No platform data.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={platformBarData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis
                  dataKey="platform"
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  tickLine={false}
                  axisLine={{ stroke: "#E5E7EB" }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#9CA3AF" }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E5E7EB",
                    fontSize: 12,
                  }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 12 }}
                />
                <Bar
                  dataKey="published"
                  name="Published"
                  stackId="a"
                  fill="#3B82F6"
                />
                <Bar
                  dataKey="approved"
                  name="Approved"
                  stackId="a"
                  fill="#10B981"
                />
                <Bar
                  dataKey="rejected"
                  name="Rejected"
                  stackId="a"
                  fill="#EF4444"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Engagement metrics */}
      {data.engagement &&
        (data.engagement.likes > 0 || data.engagement.impressions > 0) && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="card p-4 flex items-center gap-3">
              <Heart className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400">Likes</p>
                <p className="text-lg font-bold text-gray-900">
                  {data.engagement.likes.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <MessageCircle className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400">Comments</p>
                <p className="text-lg font-bold text-gray-900">
                  {data.engagement.comments.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <Repeat2 className="h-5 w-5 text-green-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400">Shares</p>
                <p className="text-lg font-bold text-gray-900">
                  {data.engagement.shares.toLocaleString()}
                </p>
              </div>
            </div>
            <div className="card p-4 flex items-center gap-3">
              <Eye className="h-5 w-5 text-purple-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400">Impressions</p>
                <p className="text-lg font-bold text-gray-900">
                  {data.engagement.impressions.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

      {/* Platform detail table */}
      {platforms.length > 0 && (
        <div className="card overflow-hidden">
          <div className="p-6 pb-3">
            <h2 className="font-semibold text-gray-800">Platform details</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-t border-b border-gray-100">
                  <th className="text-left font-medium text-gray-500 px-6 py-3">
                    Platform
                  </th>
                  <th className="text-right font-medium text-gray-500 px-6 py-3">
                    Total
                  </th>
                  <th className="text-right font-medium text-gray-500 px-6 py-3">
                    Approved
                  </th>
                  <th className="text-right font-medium text-gray-500 px-6 py-3">
                    Published
                  </th>
                  <th className="text-right font-medium text-gray-500 px-6 py-3">
                    Rejected
                  </th>
                </tr>
              </thead>
              <tbody>
                {platforms.map(([platform, stats]) => {
                  const meta = getPlatformMeta(platform);
                  return (
                    <tr key={platform} className="border-b border-gray-50">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full inline-block"
                            style={{ backgroundColor: meta.color }}
                          />
                          <span className="font-medium text-gray-900">
                            {meta.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3 text-right text-gray-600">
                        {stats.total}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-600">
                        {stats.approved}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-600">
                        {stats.published}
                      </td>
                      <td className="px-6 py-3 text-right text-gray-600">
                        {stats.rejected}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
